// TanStack Query hooks for the Time / Attendance (Timesheets) module. A
// timesheet rolls up a pay period's regular + OT hours for one person and moves
// through draft → submitted → approved → exported. Editing is allowed only while
// draft/submitted; approve is gated on timesheet.approve and export on
// timesheet.export. Mirrors packages/api/src/routes/timesheets.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateTimesheetInput,
  Timesheet,
  TimesheetResponse,
  TimesheetStatus,
  TimesheetsListResponse,
  UpdateTimesheetInput,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------- Query keys ----------

const KEY = 'timesheets';

export const timesheetKeys = {
  all: [KEY] as const,
  lists: () => [KEY, 'list'] as const,
  list: (filters: TimesheetFilters) => [KEY, 'list', { ...filters }] as const,
  detail: (id: number) => [KEY, 'detail', id] as const,
};

export interface TimesheetFilters {
  status?: TimesheetStatus;
  personnel_id?: number;
  /** period_start >= from (epoch-ms). */
  from?: number;
  /** period_end <= to (epoch-ms). */
  to?: number;
}

export function useTimesheets(filters: TimesheetFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.personnel_id !== undefined) {
    params.set('personnel_id', String(filters.personnel_id));
  }
  if (filters.from !== undefined) params.set('from', String(filters.from));
  if (filters.to !== undefined) params.set('to', String(filters.to));
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/timesheets?${qs}` : '/api/timesheets';
  return useQuery({
    queryKey: timesheetKeys.list(filters),
    queryFn: ({ signal }) => api.get<TimesheetsListResponse>(url, signal),
  });
}

export function useTimesheet(id: number | null) {
  return useQuery({
    queryKey: timesheetKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<TimesheetResponse>(`/api/timesheets/${id}`, signal),
  });
}

export function useCreateTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTimesheetInput) =>
      api.post<TimesheetResponse>('/api/timesheets', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: timesheetKeys.lists() });
    },
  });
}

export interface UpdateTimesheetArgs extends UpdateTimesheetInput {
  id: number;
}

export function useUpdateTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateTimesheetArgs) =>
      api.patch<TimesheetResponse>(`/api/timesheets/${id}`, patch),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: timesheetKeys.lists() });
      void qc.invalidateQueries({ queryKey: timesheetKeys.detail(id) });
    },
  });
}

export function useDeleteTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/timesheets/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: timesheetKeys.lists() });
    },
  });
}

// ---------- Lifecycle transitions ----------
//
// Each is a dedicated endpoint with its own permission:
//   submit  (draft → submitted)      timesheet.write
//   approve (submitted → approved)   timesheet.approve
//   export  (approved → exported)    timesheet.export
// They share the same invalidation, so a small factory produces the hooks.

function makeLifecycleHook(action: 'submit' | 'approve' | 'export') {
  return function useLifecycle() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: number) =>
        api.post<TimesheetResponse>(`/api/timesheets/${id}/${action}`, {}),
      onSuccess: (_data, id) => {
        void qc.invalidateQueries({ queryKey: timesheetKeys.lists() });
        void qc.invalidateQueries({ queryKey: timesheetKeys.detail(id) });
      },
    });
  };
}

export const useSubmitTimesheet = makeLifecycleHook('submit');
export const useApproveTimesheet = makeLifecycleHook('approve');
export const useExportTimesheet = makeLifecycleHook('export');

// Re-export the wire types pages commonly need alongside the hooks.
export type { Timesheet, TimesheetStatus };
